// src/screens/MapView.tsx
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  MapContainer,
  TileLayer,
  Marker,
  Polyline,
  Popup,
  Circle,
  CircleMarker,
  useMapEvent,
  useMap,
} from 'react-leaflet';
import L, { LatLngExpression, LatLngTuple, Map } from 'leaflet';
import 'leaflet.markercluster';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { FiSearch, FiX } from 'react-icons/fi';
import html2canvas from 'html2canvas';

import { useItinerary } from '../hooks/useItinerary';
import { CURRENT_TIMING_VERSION } from '../context/ItineraryContext';
import {
  Lugar,
  ModoTransporte,
  recalcularItinerario,
  shareItinerary,
  obtenerItinerarioCompartido,
} from '../api/api';
import { obtenerRutaOSRM, RutaOSRMGeometry } from '../api/osrm';
import { getIconForLugar, getMaterialIconHtml, houseIcon, normalizeMaterialIconName } from '../icons';
import {
  buildGpxContent,
  buildItineraryPdf,
  buildKmlContent,
  downloadTextFile,
} from '../utils/itineraryExport';

import PlaceCard from '../components/PlaceCard';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import styles from '../styles/MapView.module.css';

const SearchIcon = FiSearch as unknown as React.FC<React.SVGProps<SVGSVGElement>>;
const CloseIcon = FiX as unknown as React.FC<React.SVGProps<SVGSVGElement>>;
const wait = (ms: number) => new Promise<void>((resolve) => window.setTimeout(resolve, ms));

/* ------------------------------------------------------------------ */
/* UTILIDADES                                                        */
/* ------------------------------------------------------------------ */

/* 1) Cerrar pop-ups al pinchar sobre el mapa */
const MapClickHandler: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  useMapEvent('click', onClick);
  return null;
};

/* 2) Layer de clustering de POIs externos */
const ClusterLayer: React.FC<{
  puntos: Lugar[];
  selectedRoutePlaces: Lugar[];
  selectedNewPlace: Lugar | null;
  onMarkerClick: (l: Lugar) => void;
  onMarkerMouseOver: (l: Lugar) => void;
  onMarkerMouseOut: () => void;
}> = ({
  puntos,
  selectedRoutePlaces,
  selectedNewPlace,
  onMarkerClick,
  onMarkerMouseOver,
  onMarkerMouseOut,
}) => {
  const map = useMap();
  const layerRef = useRef<L.MarkerClusterGroup>();

  useEffect(() => {
    const clusterGroup = L.markerClusterGroup();
    layerRef.current = clusterGroup;

    puntos.filter(hasValidCoordinates).forEach((punto) => {
      const icon =
        selectedRoutePlaces.some((p) => p.id === punto.id) ||
        selectedNewPlace?.id === punto.id
          ? blueIcon
          : getIconForLugar(punto);

      const marker = L.marker([punto.latitud, punto.longitud], { icon });

      marker.on('click', () => onMarkerClick(punto));
      marker.on('mouseover', () => onMarkerMouseOver(punto));
      marker.on('mouseout', () => onMarkerMouseOut());

      clusterGroup.addLayer(marker);
    });

    map.addLayer(clusterGroup);
    return () => {
      map.removeLayer(clusterGroup);
    };
  }, [
    map,
    puntos,
    selectedRoutePlaces,
    selectedNewPlace,
    onMarkerClick,
    onMarkerMouseOver,
    onMarkerMouseOut,
  ]);

  return null;
};

/* 3) Extraer la primera foto disponible */
const getImageUrl = (fotos: any): string | undefined => {
  const foto = Array.isArray(fotos) ? fotos.find(Boolean) : fotos;
  return typeof foto === 'string' && foto.trim() ? foto : undefined;
};

const buildFallbackRouteGeometry = (
  coordinates: [number, number][],
): RutaOSRMGeometry | null => {
  const validCoordinates = coordinates.filter(
    ([lat, lon]) => Number.isFinite(lat) && Number.isFinite(lon),
  );

  if (validCoordinates.length < 2) return null;

  return {
    type: 'LineString',
    coordinates: validCoordinates.map(([lat, lon]) => [lon, lat] as [number, number]),
  };
};

const obtenerRutaConFallback = async (
  coordinates: [number, number][],
  modoTransporte: ModoTransporte,
): Promise<RutaOSRMGeometry | null> => {
  if (coordinates.length < 2) return null;
  const osrmGeometry = await obtenerRutaOSRM(coordinates, modoTransporte);
  return osrmGeometry ?? buildFallbackRouteGeometry(coordinates);
};

const normalizarTextoBusqueda = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

/* 4) Icono azul para “seleccionado” */
const hasValidCoordinates = <T extends Pick<Lugar, 'latitud' | 'longitud'>>(
  lugar?: T | null,
): lugar is T =>
  !!lugar && Number.isFinite(lugar.latitud) && Number.isFinite(lugar.longitud);

const blueIcon = new L.Icon({
  iconUrl:
    'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png',
  iconRetinaUrl:
    'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png',
  shadowUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

/* 5) Colores */
const colores = [
  '#EF3340',
  '#509E2F',
  '#F1B434',
  '#BB29BB',
  '#E48900',
  '#00AB83',
  '#E5007D',
  '#9A3324',
  '#59008E',
  '#14B5FA',
  '#0070C8',
  '#005EB8',
  '#001489',
  '#003057',
];

const DEFAULT_ROUTE_START_TIME = '10:00';
const MIN_ROUTE_START_TIME = '07:00';
const MAX_ROUTE_END_TIME = '19:00';

const isHourInputValue = (value?: string | null) => !!value && /^\d{2}:\d{2}$/.test(value);

const normalizarHoraInicioRuta = (value?: string | null) => {
  if (!isHourInputValue(value)) return DEFAULT_ROUTE_START_TIME;
  if (value! < MIN_ROUTE_START_TIME) return MIN_ROUTE_START_TIME;
  if (value! > MAX_ROUTE_END_TIME) return MAX_ROUTE_END_TIME;
  return value!;
};

const abrirSelectorHora = (input: HTMLInputElement) => {
  const inputConPicker = input as HTMLInputElement & { showPicker?: () => void };
  try {
    inputConPicker.showPicker?.();
  } catch {
    // Algunos navegadores solo permiten showPicker en una interaccion directa.
  }
};

type UserLocation = {
  latitud: number;
  longitud: number;
  precision: number;
};

const geolocationOptions: PositionOptions = {
  enableHighAccuracy: true,
  timeout: 12000,
  maximumAge: 10000,
};

const getGeolocationErrorMessage = (error: GeolocationPositionError): string => {
  switch (error.code) {
    case error.PERMISSION_DENIED:
      return 'Permiso de ubicacion denegado. Puedes activarlo desde los ajustes del navegador.';
    case error.POSITION_UNAVAILABLE:
      return 'La ubicacion no esta disponible ahora mismo.';
    case error.TIMEOUT:
      return 'No se pudo obtener tu ubicacion a tiempo. Prueba de nuevo.';
    default:
      return 'No se pudo obtener tu ubicacion.';
  }
};

const LocationButtonIcon: React.FC<{ disabled?: boolean }> = ({ disabled = false }) => (
  <svg
    className={styles.locationButtonIcon}
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
  >
    <circle cx="12" cy="12" r="4" fill="none" stroke="currentColor" strokeWidth="2" />
    <path
      d="M12 3v3M12 18v3M3 12h3M18 12h3"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="2"
    />
    <circle cx="12" cy="12" r="1.4" fill="currentColor" />
    {disabled && (
      <path
        d="M4 4l16 16"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="2.4"
      />
    )}
  </svg>
);

const getLeafletIconMarkup = (baseIcon: L.Icon | L.DivIcon, directusIcon?: string | null): string => {
  const materialIcon = normalizeMaterialIconName(directusIcon);
  if (materialIcon) return getMaterialIconHtml(materialIcon, '#111111', 40, 14);

  const divIconHtml = (baseIcon as L.DivIcon).options.html;
  if (typeof divIconHtml === 'string') return divIconHtml;

  const iconUrl = (baseIcon as L.Icon).options.iconUrl;
  return typeof iconUrl === 'string'
    ? `<img src="${iconUrl}" alt="icon" />`
    : getMaterialIconHtml('place');
};

/* 6) Crear icono numerado */
const createNumberedIcon = (
  numero: number,
  baseIcon: L.Icon | L.DivIcon,
  routeColor: string,
  directusIcon?: string | null,
) =>
  new L.DivIcon({
    html: `<div class="${styles.numberedIconContainer}">
             ${getLeafletIconMarkup(baseIcon, directusIcon)}
             <span class="${styles.number}" style="color: ${routeColor};">${numero}</span>
           </div>`,
    className: styles.customNumberedDivIcon,
    iconSize: baseIcon.options.iconSize,
    iconAnchor: [12, 41],
    popupAnchor: [0, -40],
  });

/** Duración aproximada solo con horas HH:mm  */
const HHMM_PATTERN = /^\d{2}:\d{2}$/;
const isHHMM = (value?: string | null): value is string => !!value && HHMM_PATTERN.test(value);

const calcularDuracionRuta = (lugares: Lugar[]): string => {
  if (!lugares.length) return '—';

  const first = lugares[0];
  const last = lugares[lugares.length - 1];

  if (!isHHMM(first.horaLlegada) || !isHHMM(last.horaSalida)) return '—';

  const [h1, m1] = first.horaLlegada!.split(':').map(Number);
  const [h2, m2] = last.horaSalida!.split(':').map(Number);

  const startMin = h1 * 60 + m1;
  const endMin = h2 * 60 + m2;
  const diff = endMin - startMin;

  if (diff <= 0) return '—';
  return `${Math.floor(diff / 60)}h ${diff % 60}m`;
};

/* 8) Hook para exponer la ref del mapa */
const SetMapRef: React.FC<{ mapRef: React.RefObject<Map> }> = ({ mapRef }) => {
  const map = useMap();
  useEffect(() => {
    (mapRef as React.MutableRefObject<Map | null>).current = map;
  }, [map, mapRef]);
  return null;
};

/* ------------------------------------------------------------------ */
/* COMPONENTE                                                         */
/* ------------------------------------------------------------------ */
let mapViewInstanceCounter = 0;

const MapView: React.FC = () => {
  const [showShareModal, setShowShareModal] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [shareUrl, setShareUrl] = useState('');
  const [copyingShareLink, setCopyingShareLink] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [exportingRoute, setExportingRoute] = useState<'gpx' | 'kml' | null>(null);

  const navigate = useNavigate();
  const { shareId } = useParams<{ shareId?: string }>();

  const {
    itinerarios: rawItinerarios,
    puntosTuristicos,
    setItinerarios,
    startingPoint,
    modoTransporte,
    timingVersion,
    setTimingVersion,
    fechaInicio: fechaInicioGuardada,
    dayStarts,
    dayEnds,
    resetItinerary,
  } = useItinerary();
  const poiDetailsById = useMemo(() => {
    const details = new globalThis.Map<number, Lugar>();
    puntosTuristicos.forEach((punto) => details.set(punto.id, punto));
    return details;
  }, [puntosTuristicos]);

  const itinerarios = useMemo(
    () =>
      Array.isArray(rawItinerarios)
        ? rawItinerarios.map((itinerario) => ({
            ...itinerario,
            lugares: itinerario.lugares.map((lugar) => {
              const details = poiDetailsById.get(lugar.id);
              if (!details) return lugar;

              return {
                ...details,
                ...lugar,
                descripcion: lugar.descripcion ?? details.descripcion,
                icon: lugar.icon ?? details.icon,
                fotos: lugar.fotos ?? details.fotos,
                horario: lugar.horario ?? details.horario,
              };
            }),
          }))
        : [],
    [rawItinerarios, poiDetailsById],
  );

  const location = useLocation() as any;
  const mapRef = useRef<Map | null>(null) as React.MutableRefObject<Map | null>;
  const isViewActiveRef = useRef(true);
  const instanceIdRef = useRef(++mapViewInstanceCounter);
  const log = useCallback((message: string, data?: unknown) => {
    if (data === undefined) {
      console.log(`[MapView ${instanceIdRef.current}] ${message}`);
    } else {
      console.log(`[MapView ${instanceIdRef.current}] ${message}`, data);
    }
  }, []);

  /* ---- State ---- */
  const [rutasOSRM, setRutasOSRM] = useState<Array<RutaOSRMGeometry | null>>([]);
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [selectedRoutePlaces, setSelectedRoutePlaces] = useState<Lugar[]>([]);
  const [selectedNewPlace, setSelectedNewPlace] = useState<Lugar | null>(null);
  const [warningMessage, setWarningMessage] = useState<string | null>(null);
  const [noticeTone, setNoticeTone] = useState<'error' | 'success'>('error');
  const [dayStartTimes, setDayStartTimes] = useState<Record<number, string>>({});
  const [dayStartEditor, setDayStartEditor] = useState<{
    dia: number;
    left: number;
    top: number;
  } | null>(null);

  /* Tooltips para los botones */
  const [showRecalculateTooltip, setShowRecalculateTooltip] = useState(false);
  const [showDeleteTooltip, setShowDeleteTooltip] = useState(false);
  const [showSustituirTooltip, setShowSustituirTooltip] = useState(false);
  const [showAddTooltip, setShowAddTooltip] = useState(false);

  const [draggedDay, setDraggedDay] = useState<number | null>(null);
  const [activePlace, setActivePlace] = useState<Lugar | null>(null);
  const [placeCardPlace, setPlaceCardPlace] = useState<Lugar | null>(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [captureMode, setCaptureMode] = useState(false);
  const [showUserLocation, setShowUserLocation] = useState(false);
  const [isLocatingUser, setIsLocatingUser] = useState(false);
  const [userLocation, setUserLocation] = useState<UserLocation | null>(null);
  const [showPoiSearch, setShowPoiSearch] = useState(false);
  const [poiSearchQuery, setPoiSearchQuery] = useState('');

  /* --- Cierre automático de popups al salir con el ratón --- */
  const closePopupTimer = useRef<number | null>(null);
  const closeDayStartEditorTimer = useRef<number | null>(null);
  const refreshedPersistedTimesRef = useRef(false);
  const isDayStartTimePickerOpen = useRef(false);
  const isDayStartEditorPinnedOpen = useRef(false);
  const dayStartPanelRef = useRef<HTMLFormElement | null>(null);
  const userLocationWatchId = useRef<number | null>(null);
  const centerOnNextUserLocation = useRef(false);
  const poiSearchInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    isViewActiveRef.current = true;
    log('mount', {
      shareId,
      itineraryDays: itinerarios.length,
      locationState: location.state,
    });
    return () => {
      log('unmount');
      isViewActiveRef.current = false;
      if (closeDayStartEditorTimer.current !== null) {
        window.clearTimeout(closeDayStartEditorTimer.current);
      }
      isDayStartTimePickerOpen.current = false;
      isDayStartEditorPinnedOpen.current = false;
    };
    // Solo queremos marcar vida/muerte real de esta instancia.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!showPoiSearch) return;
    window.setTimeout(() => poiSearchInputRef.current?.focus(), 0);
  }, [showPoiSearch]);

  useEffect(() => {
    if (!dayStartEditor) return;

    const handleOutsidePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (target instanceof Node && dayStartPanelRef.current?.contains(target)) return;

      isDayStartTimePickerOpen.current = false;
      isDayStartEditorPinnedOpen.current = false;
      if (closeDayStartEditorTimer.current !== null) {
        window.clearTimeout(closeDayStartEditorTimer.current);
        closeDayStartEditorTimer.current = null;
      }
      setDayStartEditor(null);
    };

    document.addEventListener('pointerdown', handleOutsidePointerDown, true);
    return () => {
      document.removeEventListener('pointerdown', handleOutsidePointerDown, true);
    };
  }, [dayStartEditor]);

  const cancelClosePopup = () => {
    if (closePopupTimer.current) {
      window.clearTimeout(closePopupTimer.current);
      closePopupTimer.current = null;
    }
  };

  const stopUserLocationTracking = useCallback(() => {
    if (userLocationWatchId.current !== null && 'geolocation' in navigator) {
      navigator.geolocation.clearWatch(userLocationWatchId.current);
      userLocationWatchId.current = null;
    }

    centerOnNextUserLocation.current = false;
    setShowUserLocation(false);
    setIsLocatingUser(false);
    setUserLocation(null);
  }, []);

  const handleUserLocationSuccess = useCallback((position: GeolocationPosition) => {
    const nextLocation = {
      latitud: position.coords.latitude,
      longitud: position.coords.longitude,
      precision: position.coords.accuracy || 0,
    };

    setUserLocation(nextLocation);
    setShowUserLocation(true);
    setIsLocatingUser(false);

    if (centerOnNextUserLocation.current && mapRef.current) {
      const zoom = Math.max(mapRef.current.getZoom(), 15);
      mapRef.current.setView([nextLocation.latitud, nextLocation.longitud], zoom, {
        animate: true,
      });
      centerOnNextUserLocation.current = false;
    }
  }, []);

  const handleUserLocationError = useCallback((error: GeolocationPositionError) => {
    if (userLocationWatchId.current !== null && 'geolocation' in navigator) {
      navigator.geolocation.clearWatch(userLocationWatchId.current);
      userLocationWatchId.current = null;
    }

    centerOnNextUserLocation.current = false;
    setShowUserLocation(false);
    setIsLocatingUser(false);
    setUserLocation(null);
    setWarningMessage(getGeolocationErrorMessage(error));
  }, []);

  const toggleUserLocation = () => {
    if (showUserLocation || isLocatingUser) {
      stopUserLocationTracking();
      return;
    }

    if (!('geolocation' in navigator)) {
      setWarningMessage('Tu navegador no permite geolocalizacion.');
      return;
    }

    centerOnNextUserLocation.current = true;
    setIsLocatingUser(true);
    try {
      userLocationWatchId.current = navigator.geolocation.watchPosition(
        handleUserLocationSuccess,
        handleUserLocationError,
        geolocationOptions,
      );
    } catch {
      centerOnNextUserLocation.current = false;
      setIsLocatingUser(false);
      setWarningMessage('No se pudo iniciar la geolocalizacion en este navegador.');
    }
  };

  useEffect(() => {
    return () => {
      stopUserLocationTracking();
    };
  }, [stopUserLocationTracking]);

  const formatearFechaRuta = (dia: number): string => {
    const fecha = obtenerFechaRuta(dia);
    if (!fecha) return `Dia ${dia}`;

    return `Dia ${dia} - ${fecha.toLocaleDateString('es-ES', {
      weekday: 'long',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })}`;
  };

  const obtenerFechaRuta = (dia: number): Date | null => {
    const fechaInicio = location.state?.fechaInicio ?? fechaInicioGuardada;
    if (!fechaInicio) return null;

    const fecha = new Date(`${fechaInicio}T00:00:00`);
    if (isNaN(fecha.getTime())) return null;

    fecha.setDate(fecha.getDate() + dia - 1);
    return fecha;
  };

  const buscarVisitaProgramada = (lugar: Lugar) => {
    const itinerariosOrdenados =
      selectedDay !== null
        ? [
            ...itinerarios.filter((it) => it.dia === selectedDay),
            ...itinerarios.filter((it) => it.dia !== selectedDay),
          ]
        : itinerarios;

    for (const itinerario of itinerariosOrdenados) {
      const visita = itinerario.lugares.find((l) => l.id === lugar.id);
      if (visita) return { dia: itinerario.dia, visita };
    }

    return null;
  };

  const minutosDesdeMedianoche = (fecha: Date) => fecha.getHours() * 60 + fecha.getMinutes();

  const crearFechaConHora = (
    valor: Date | string | null | undefined,
    fechaBase?: Date | null,
  ): Date | null => {
    if (!valor) return null;

    if (typeof valor === 'string' && isHHMM(valor)) {
      const [horas, minutos] = valor.split(':').map(Number);
      const fecha = fechaBase ? new Date(fechaBase) : new Date();
      fecha.setHours(horas, minutos, 0, 0);
      return fecha;
    }

    const fecha = new Date(valor);
    return isNaN(fecha.getTime()) ? null : fecha;
  };

  const normalizarDiaHorario = (dia: string) =>
    dia
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .slice(0, 3);

  const diaHorarioDesdeFecha = (fecha: Date) =>
    ['dom', 'lun', 'mar', 'mie', 'jue', 'vie', 'sab'][fecha.getDay()];

  const extraerTramoHorario = (tramo?: string | null) => {
    if (!tramo) return null;
    const limpio = tramo.trim();
    if (/cerrado/i.test(limpio)) {
      return { cerradoTodoElDia: true, horarioDia: limpio };
    }

    const match = limpio.match(/(\d{1,2}):(\d{2})\s*[-–—]\s*(\d{1,2}):(\d{2})/);
    if (!match) return null;

    const [, ah, am, ch, cm] = match;
    const apertura = Number(ah) * 60 + Number(am);
    const cierre = Number(ch) * 60 + Number(cm);

    if (!Number.isFinite(apertura) || !Number.isFinite(cierre)) return null;
    return { apertura, cierre, cerradoTodoElDia: false, horarioDia: limpio };
  };

  const extraerTramosHorario = (tramo?: string | null) => {
    if (!tramo) return null;
    const limpio = tramo.trim();
    if (/cerrado/i.test(limpio)) {
      return { cerradoTodoElDia: true, horarioDia: limpio };
    }

    const tramos = limpio
      .split('/')
      .map((parte) => parte.trim())
      .filter(Boolean)
      .map((parte) => {
        const parsed = extraerTramoHorario(parte);
        if (!parsed || !('apertura' in parsed) || !('cierre' in parsed)) return null;
        return { apertura: parsed.apertura, cierre: parsed.cierre };
      })
      .filter((item): item is { apertura: number; cierre: number } => Boolean(item));

    if (!tramos.length) return null;
    return { tramos, cerradoTodoElDia: false, horarioDia: limpio };
  };

  const parsearHorario = (horario?: string | null, fechaReferencia?: Date | null) => {
    if (!horario) return null;

    const segmentos = horario
      .split(/[;|]/)
      .map((segmento) => segmento.trim())
      .filter(Boolean);

    const horarioSemanal = segmentos
      .map((segmento) => {
        const matchDia = segmento.match(/^([^:]+):\s*(.+)$/);
        if (!matchDia) return null;
        return {
          dia: normalizarDiaHorario(matchDia[1]),
          tramo: matchDia[2].trim(),
        };
      })
      .filter((item): item is { dia: string; tramo: string } => Boolean(item));

    if (horarioSemanal.length > 0) {
      const fecha = fechaReferencia ?? new Date();
      const diaBuscado = diaHorarioDesdeFecha(fecha);
      const tramoDia = horarioSemanal.find((item) => item.dia === diaBuscado);

      if (!tramoDia) {
        return {
          cerradoTodoElDia: false,
          horarioDia: null,
          sinHorarioParaDia: true,
        };
      }

      const parsed = extraerTramosHorario(tramoDia.tramo);
      return parsed ? { ...parsed, dia: diaBuscado } : null;
    }

    return extraerTramosHorario(horario);
  };

  const obtenerEstadoHorario = (lugar: Lugar) => {
    const visitaProgramada = buscarVisitaProgramada(lugar);
    const visita = visitaProgramada?.visita ?? lugar;
    const horario = visita.horario || lugar.horario || null;
    const fechaRutaDate = visitaProgramada ? obtenerFechaRuta(visitaProgramada.dia) : null;
    const llegada = crearFechaConHora(visita.horaLlegada, fechaRutaDate);
    const salida = crearFechaConHora(visita.horaSalida, fechaRutaDate);
    const fechaReferencia = fechaRutaDate ?? llegada;
    const horarioParseado = parsearHorario(horario, fechaReferencia);

    if (!visitaProgramada) {
      return {
        horario,
        fechaRuta: null,
        visitaLabel: 'Sin hora estimada en ruta',
        aviso: horario ? 'Selecciona este punto en una ruta para comprobar apertura.' : null,
        cerrado: false,
      };
    }

    const fechaRuta = formatearFechaRuta(visitaProgramada.dia);
    const horaValida = llegada && salida && !isNaN(llegada.getTime()) && !isNaN(salida.getTime());

    if (!horaValida) {
      return {
        horario,
        fechaRuta,
        visitaLabel: 'Hora estimada no disponible',
        aviso: null,
        cerrado: false,
      };
    }

    const visitaLabel = `${formatearHora(llegada)} - ${formatearHora(salida)}`;

    if (!horario) {
      return {
        horario,
        fechaRuta,
        visitaLabel,
        aviso: 'Horario no disponible para comprobar apertura.',
        cerrado: false,
      };
    }

    if (!horarioParseado) {
      return {
        horario,
        fechaRuta,
        visitaLabel,
        aviso: 'Horario no interpretable automaticamente.',
        cerrado: false,
      };
    }

    if ('sinHorarioParaDia' in horarioParseado && horarioParseado.sinHorarioParaDia) {
      return {
        horario,
        horarioDia: null,
        fechaRuta,
        visitaLabel,
        aviso: 'No hay horario registrado para ese dia de la semana.',
        cerrado: false,
      };
    }

    if (horarioParseado.cerradoTodoElDia) {
      return {
        horario,
        horarioDia: horarioParseado.horarioDia,
        fechaRuta,
        visitaLabel,
        aviso: 'Aviso: este punto figura como cerrado ese dia.',
        cerrado: true,
      };
    }

    if (!('tramos' in horarioParseado)) {
      return {
        horario,
        horarioDia: horarioParseado.horarioDia,
        fechaRuta,
        visitaLabel,
        aviso: 'Horario no interpretable automaticamente.',
        cerrado: false,
      };
    }

    const llegadaMin = minutosDesdeMedianoche(llegada);
    const salidaMin = minutosDesdeMedianoche(salida);
    const tramosHorario: Array<{ apertura: number; cierre: number }> =
      'tramos' in horarioParseado && horarioParseado.tramos ? horarioParseado.tramos : [];
    const dentroHorario = tramosHorario.some(
      ({ apertura, cierre }) => llegadaMin >= apertura && salidaMin <= cierre,
    );

    return {
      horario,
      horarioDia: horarioParseado.horarioDia,
      fechaRuta,
      visitaLabel,
      aviso: dentroHorario
        ? 'Abierto durante la visita estimada.'
        : 'Aviso: podria estar cerrado en la hora estimada de visita.',
      cerrado: !dentroHorario,
    };
  };

  const scheduleClosePopup = () => {
    cancelClosePopup();
    closePopupTimer.current = window.setTimeout(() => {
      setActivePlace(null);
    }, 150);
  };

  const cancelCloseDayStartEditor = () => {
    if (closeDayStartEditorTimer.current !== null) {
      window.clearTimeout(closeDayStartEditorTimer.current);
      closeDayStartEditorTimer.current = null;
    }
  };

  const scheduleCloseDayStartEditor = () => {
    cancelCloseDayStartEditor();
    closeDayStartEditorTimer.current = window.setTimeout(() => {
      if (isDayStartTimePickerOpen.current || isDayStartEditorPinnedOpen.current) return;
      setDayStartEditor(null);
    }, 180);
  };

  const keepDayStartEditorOpenForTimePicker = () => {
    isDayStartTimePickerOpen.current = true;
    cancelCloseDayStartEditor();
  };

  const releaseDayStartTimePicker = () => {
    window.setTimeout(() => {
      isDayStartTimePickerOpen.current = false;
    }, 150);
  };

  const openDayStartEditor = (element: HTMLElement, dia: number) => {
    if (dayStartEditor?.dia !== dia) {
      isDayStartEditorPinnedOpen.current = false;
    }
    cancelCloseDayStartEditor();
    const rect = element.getBoundingClientRect();
    const panelWidth = 190;
    const viewportPadding = 8;
    setDayStartEditor({
      dia,
      left: Math.max(
        viewportPadding,
        Math.min(rect.left, window.innerWidth - panelWidth - viewportPadding),
      ),
      top: rect.bottom + 8,
    });
  };

  /* ---- Centro inicial ---- */
  const centroMapa: LatLngExpression = startingPoint
    ? [startingPoint.latitud, startingPoint.longitud]
    : puntosTuristicos.some(hasValidCoordinates)
      ? [
          puntosTuristicos.find(hasValidCoordinates)!.latitud,
          puntosTuristicos.find(hasValidCoordinates)!.longitud,
        ]
      : [42.7654, -3.0924];

  /* Refs para hacer scroll a los items del itinerario */
  const itineraryItemRefs = useRef<Record<number, HTMLDivElement | null>>({});

  const lugaresRutaDiaSeleccionado = useMemo(() => {
    const ids = new Set<number>();
    if (selectedDay === null) return ids;

    itinerarios
      .find((it) => it.dia === selectedDay)
      ?.lugares.forEach((l) => ids.add(l.id));

    return ids;
  }, [itinerarios, selectedDay]);

  const lugaresEnRutas = useMemo(() => {
    const ids = new Set<number>();

    itinerarios.forEach((it) => {
      it.lugares.forEach((l) => ids.add(l.id));
    });

    return ids;
  }, [itinerarios]);

  const puntosVisiblesParaCambios = useMemo(
    () => puntosTuristicos.filter((pt) => !lugaresEnRutas.has(pt.id)),
    [puntosTuristicos, lugaresEnRutas],
  );

  const poisBuscables = useMemo(() => {
    const porId = new globalThis.Map<number, Lugar>();

    puntosTuristicos.forEach((p) => porId.set(p.id, p));
    itinerarios.forEach((it) => {
      it.lugares.forEach((l) => porId.set(l.id, l));
    });

    return Array.from(porId.values()).sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
  }, [itinerarios, puntosTuristicos]);

  const resultadosBusquedaPoi = useMemo(() => {
    const query = normalizarTextoBusqueda(poiSearchQuery);
    if (!query) return poisBuscables.slice(0, 8);

    return poisBuscables
      .filter((poi) => normalizarTextoBusqueda(poi.nombre).includes(query))
      .slice(0, 8);
  }, [poiSearchQuery, poisBuscables]);

  const formatearHora = (fecha: Date | string | null | undefined): string => {
    if (!fecha) return 'N/A';
    if (typeof fecha === 'string' && isHHMM(fecha)) return fecha;
    const d = new Date(fecha);
    if (isNaN(d.getTime())) return 'Hora inválida';
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const extraerHoraInput = (fecha: Date | string | null | undefined): string | null => {
    if (!fecha) return null;
    if (typeof fecha === 'string' && isHHMM(fecha)) return fecha;
    const d = new Date(fecha);
    if (isNaN(d.getTime())) return null;
    const horas = String(d.getHours()).padStart(2, '0');
    const minutos = String(d.getMinutes()).padStart(2, '0');
    return `${horas}:${minutos}`;
  };

  const getHoraInicioDia = useCallback(
    (dia: number) => {
      const horaGuardada = dayStartTimes[dia];
      if (horaGuardada) return horaGuardada;

      const primeraLlegada = itinerarios.find((it) => it.dia === dia)?.lugares[0]?.horaLlegada;
      return extraerHoraInput(primeraLlegada) ?? DEFAULT_ROUTE_START_TIME;
    },
    [dayStartTimes, itinerarios],
  );

  const buscarDiaConLugar = useCallback(
    (lugarId: number, diaExcluido?: number) =>
      itinerarios.find(
        (it) =>
          it.dia !== diaExcluido &&
          it.lugares.some((lugar) => lugar.id === lugarId),
      ),
    [itinerarios],
  );

  useEffect(() => {
    if (shareId) return;
    if (refreshedPersistedTimesRef.current) return;
    if (!itinerarios.length) return;
    if (timingVersion === CURRENT_TIMING_VERSION) return;

    let cancelado = false;
    refreshedPersistedTimesRef.current = true;

    (async () => {
      try {
        log('refreshPersistedTimes:start', {
          current: timingVersion,
          next: CURRENT_TIMING_VERSION,
        });

        const recalculados = await Promise.all(
          itinerarios.map((it) =>
            recalcularItinerario(
              it.dia,
              it.lugares,
              null,
              null,
              modoTransporte,
              getHoraInicioDia(it.dia),
            ),
          ),
        );

        if (cancelado || !isViewActiveRef.current) return;

        setItinerarios(recalculados);
        setTimingVersion(CURRENT_TIMING_VERSION);
        log('refreshPersistedTimes:done', { days: recalculados.length });
      } catch (error) {
        refreshedPersistedTimesRef.current = false;
        console.error('[MapView] refreshPersistedTimes:error', error);
      }
    })();

    return () => {
      cancelado = true;
    };
  }, [
    getHoraInicioDia,
    itinerarios,
    log,
    modoTransporte,
    setItinerarios,
    setTimingVersion,
    shareId,
    timingVersion,
  ]);

  useEffect(() => {
    if (!itinerarios.length) {
      setDayStartTimes({});
      return;
    }

    setDayStartTimes((prev) => {
      const next: Record<number, string> = {};
      itinerarios.forEach((it) => {
        next[it.dia] =
          prev[it.dia] ??
          extraerHoraInput(it.lugares[0]?.horaLlegada) ??
          DEFAULT_ROUTE_START_TIME;
      });

      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(next);
      const unchanged =
        prevKeys.length === nextKeys.length &&
        nextKeys.every((key) => prev[Number(key)] === next[Number(key)]);

      return unchanged ? prev : next;
    });
  }, [itinerarios]);

  useEffect(() => {
    if (!itinerarios.length) return;

    const idsVistos = new Set<number>();
    const diasAfectados = new Set<number>();
    let hayDuplicados = false;

    const itinerariosSinDuplicados = itinerarios.map((it) => {
      const lugares = it.lugares.filter((lugar) => {
        if (idsVistos.has(lugar.id)) {
          hayDuplicados = true;
          diasAfectados.add(it.dia);
          return false;
        }

        idsVistos.add(lugar.id);
        return true;
      });

      return lugares.length === it.lugares.length ? it : { ...it, lugares };
    });

    if (!hayDuplicados) return;

    let cancelado = false;

    (async () => {
      log('dedupeItinerary:start', { diasAfectados: Array.from(diasAfectados) });

      const recalculados = await Promise.all(
        itinerariosSinDuplicados.map(async (it) => {
          if (!diasAfectados.has(it.dia)) return it;

          try {
            return await recalcularItinerario(
              it.dia,
              it.lugares,
              null,
              null,
              modoTransporte,
              getHoraInicioDia(it.dia),
            );
          } catch (error) {
            console.error('[MapView] dedupeItinerary:error', error);
            return it;
          }
        }),
      );

      if (cancelado || !isViewActiveRef.current) return;

      setItinerarios(recalculados);
      setSelectedNewPlace(null);
      setSelectedRoutePlaces([]);
      setNoticeTone('error');
      setWarningMessage('Un punto no puede estar en mas de un dia. He eliminado los duplicados de los dias posteriores.');
    })();

    return () => {
      cancelado = true;
    };
  }, [getHoraInicioDia, itinerarios, log, modoTransporte, setItinerarios]);

  /* ------------------------------------------------------------------ */
  /* 1) Cargar rutas OSRM                                                */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    let cancelled = false;

    const fetchRutas = async () => {
      log('fetchRutas:start', {
        days: itinerarios.map((it) => ({ dia: it.dia, lugares: it.lugares.length })),
        hasStartingPoint: !!startingPoint,
      });
      const geoms = await Promise.all(
        itinerarios.map(async (it) => {
          let coords: [number, number][] = it.lugares
            .filter(hasValidCoordinates)
            .map((l) => [l.latitud, l.longitud]);

          const dStart = dayStarts[it.dia];
          const dEnd = dayEnds[it.dia];
          if (hasValidCoordinates(dStart)) coords = [[dStart.latitud, dStart.longitud], ...coords];
          if (hasValidCoordinates(dEnd)) coords = [...coords, [dEnd.latitud, dEnd.longitud]];

          if (hasValidCoordinates(startingPoint)) {
            coords = [
              [startingPoint.latitud, startingPoint.longitud],
              ...coords,
              [startingPoint.latitud, startingPoint.longitud],
            ];
          }

          return await obtenerRutaConFallback(coords, modoTransporte);
        }),
      );

      if (cancelled || !isViewActiveRef.current) {
        log('fetchRutas:ignored', { cancelled, active: isViewActiveRef.current });
        return;
      }
      log('fetchRutas:done', {
        geometries: geoms.map((geo) => geo?.coordinates.length ?? 0),
      });
      setRutasOSRM(geoms);
    };

    if (itinerarios.length) {
      fetchRutas();
      if (selectedDay === null) setSelectedDay(itinerarios[0].dia);
    } else {
      setRutasOSRM([]);
    }

    return () => {
      cancelled = true;
    };
  }, [itinerarios, startingPoint, dayStarts, dayEnds, modoTransporte, log]);

  /* ------------------------------------------------------------------ */
  /* 2) Ajustar vista al seleccionar día                                 */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    if (selectedDay === null || !mapRef.current) return;
    const idx = itinerarios.findIndex((it) => it.dia === selectedDay);
    const selectedGeometry = idx >= 0 ? rutasOSRM[idx] : null;
    if (!selectedGeometry) return;
    const pos = selectedGeometry.coordinates.map(([lon, lat]) => [lat, lon] as [number, number]);
    mapRef.current.fitBounds(L.latLngBounds(pos), {
      paddingTopLeft: [50, 50],
      paddingBottomRight: [50, 150],
    });
  }, [selectedDay, itinerarios, rutasOSRM]);

  useEffect(() => {
    log('state', {
      itineraryDays: itinerarios.length,
      selectedDay,
      routeGeometries: rutasOSRM.map((geo) => geo?.coordinates.length ?? 0),
      visiblePois: puntosVisiblesParaCambios.length,
    });
  }, [itinerarios.length, log, puntosVisiblesParaCambios.length, rutasOSRM, selectedDay]);

  /* 0) Si llegamos desde un enlace “compartido”, traemos los datos */
  useEffect(() => {
    if (!shareId) return;

    (async () => {
      try {
        log('share:load:start', { shareId });
        const its = await obtenerItinerarioCompartido(shareId);
        if (!isViewActiveRef.current) {
          log('share:load:ignored');
          return;
        }
        log('share:load:done', { days: its.length });
        setItinerarios(its);
        if (its.length) setSelectedDay(its[0].dia);
      } catch (err) {
        console.error('Error cargando itinerario compartido', err);
        if (isViewActiveRef.current) alert('No se pudo cargar el itinerario compartido.');
      }
    })();
  }, [log, setItinerarios, shareId]);

  /* ------------------------------------------------------------------ */
  /* 3) Limpiar selecciones al cambiar de día                            */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    setSelectedNewPlace(null);
    setSelectedRoutePlaces([]);
    setWarningMessage(null);
  }, [selectedDay]);

  /* ------------------------------------------------------------------ */
  /*  Drag & drop días                                                   */
  /* ------------------------------------------------------------------ */
  const handleDragStart = (_e: React.DragEvent<HTMLElement>, day: number) => setDraggedDay(day);
  const handleDragOver = (e: React.DragEvent<HTMLElement>) => e.preventDefault();
  const handleDrop = (_e: React.DragEvent<HTMLElement>, targetDay: number) => {
    if (draggedDay === null || draggedDay === targetDay) return setDraggedDay(null);
    const i1 = itinerarios.findIndex((it) => it.dia === draggedDay);
    const i2 = itinerarios.findIndex((it) => it.dia === targetDay);
    if (i1 < 0 || i2 < 0) return setDraggedDay(null);
    const copy = [...itinerarios];
    [copy[i1].dia, copy[i2].dia] = [copy[i2].dia, copy[i1].dia];
    copy.sort((a, b) => a.dia - b.dia);
    setItinerarios(copy);
    setDraggedDay(null);
  };

  /* ------------------------------------------------------------------ */
  /*  Selecciones de markers                                             */
  /* ------------------------------------------------------------------ */
  const handleRouteMarkerClick = (l: Lugar) => {
    if (captureMode) return;
    setSelectedRoutePlaces((prev) => {
      if (prev.some((p) => p.id === l.id)) return prev.filter((p) => p.id !== l.id);
      const next = [...prev, l];
      if (next.length > 2) next.shift();
      return next;
    });
    itineraryItemRefs.current[l.id]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const handleExternalMarkerClick = (place: Lugar) => {
    if (lugaresRutaDiaSeleccionado.has(place.id)) {
      handleRouteMarkerClick(place);
      return;
    }

    setSelectedNewPlace((prev) => (prev?.id === place.id ? null : place));
  };

  const handleNewPlaceClick = (l: Lugar) => {
    if (captureMode) return;
    setSelectedNewPlace((prev) => (prev?.id === l.id ? null : l));
  };

  const seleccionarPoiDesdeBusqueda = (poi: Lugar) => {
    if (captureMode) return;

    const diaRuta = itinerarios.find((it) => it.lugares.some((l) => l.id === poi.id));
    const poiEnRuta = diaRuta?.lugares.find((l) => l.id === poi.id) ?? null;
    const poiSeleccionado = poiEnRuta ?? poi;

    if (diaRuta && poiEnRuta) {
      setSelectedDay(diaRuta.dia);
      setSelectedRoutePlaces([poiEnRuta]);
      setSelectedNewPlace(null);
    } else {
      setSelectedRoutePlaces([]);
      setSelectedNewPlace(poi);
    }

    setActivePlace(poiSeleccionado);
    setWarningMessage(null);
    setShowPoiSearch(false);
    setPoiSearchQuery(poiSeleccionado.nombre);

    if (hasValidCoordinates(poiSeleccionado)) {
      mapRef.current?.setView([poiSeleccionado.latitud, poiSeleccionado.longitud], 16, {
        animate: true,
      });
    }

    itineraryItemRefs.current[poiSeleccionado.id]?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  };

  const isMarkerSelected = (l: Lugar) =>
    selectedRoutePlaces.some((p) => p.id === l.id) || selectedNewPlace?.id === l.id;

  /* ------------------------------------------------------------------ */
  /*  Flags de disponibilidad de botones                                 */
  /* ------------------------------------------------------------------ */
  const isRecalculateDisabled = !(selectedRoutePlaces.length === 1 && selectedNewPlace);
  const isDeleteDisabled = !(selectedRoutePlaces.length === 1 && !selectedNewPlace);

  const itinerarioSeleccionado =
    selectedDay !== null ? itinerarios.find((it) => it.dia === selectedDay) : null;

  /* ------------------------------------------------------------------ */
  /*  Acciones de botones                                                */
  /* ------------------------------------------------------------------ */
  const eliminarPuntoSeleccionado = async () => {
    if (!itinerarioSeleccionado || selectedRoutePlaces.length !== 1) return;
    try {
      const it = await recalcularItinerario(
        itinerarioSeleccionado.dia,
        itinerarioSeleccionado.lugares,
        selectedRoutePlaces[0],
        null,
        modoTransporte,
        getHoraInicioDia(itinerarioSeleccionado.dia),
      );
      if (!isViewActiveRef.current) return;
      setItinerarios((prev) => prev.map((x) => (x.dia === it.dia ? it : x)));
      setSelectedRoutePlaces([]);
      setWarningMessage(null);
    } catch {
      setWarningMessage('Error al eliminar.');
    }
  };

  const intercambiarLugares = async () => {
    if (!itinerarioSeleccionado || selectedRoutePlaces.length !== 1 || !selectedNewPlace) {
      return setWarningMessage('Selecciona 1 dentro y 1 fuera.');
    }

    const diaOcupado = buscarDiaConLugar(selectedNewPlace.id, itinerarioSeleccionado.dia);
    if (diaOcupado) {
      setNoticeTone('error');
      setSelectedNewPlace(null);
      return setWarningMessage(`Ese punto ya esta incluido en el dia ${diaOcupado.dia}.`);
    }

    try {
      const [oldL] = selectedRoutePlaces;
      const nuevos = itinerarioSeleccionado.lugares.map((l) => (l.id === oldL.id ? selectedNewPlace : l));
      const it = await recalcularItinerario(
        itinerarioSeleccionado.dia,
        nuevos,
        oldL,
        selectedNewPlace,
        modoTransporte,
        getHoraInicioDia(itinerarioSeleccionado.dia),
      );
      if (!isViewActiveRef.current) return;
      setItinerarios((prev) => prev.map((x) => (x.dia === it.dia ? it : x)));
      setSelectedNewPlace(null);
      setSelectedRoutePlaces([]);
      setWarningMessage(null);
    } catch {
      setWarningMessage('Error al intercambiar.');
    }
  };

  const agregarPuntoSeleccionado = async () => {
    if (!itinerarioSeleccionado || !selectedNewPlace) {
      setNoticeTone('error');
      return setWarningMessage('Selecciona un punto fuera de la ruta para anadirlo.');
    }

    const diaOcupado = buscarDiaConLugar(selectedNewPlace.id, itinerarioSeleccionado.dia);
    if (diaOcupado) {
      setNoticeTone('error');
      setSelectedNewPlace(null);
      return setWarningMessage(`Ese punto ya esta incluido en el dia ${diaOcupado.dia}.`);
    }

    const di = itinerarios.findIndex((it) => it.dia === itinerarioSeleccionado.dia);
    if (di === -1) {
      setNoticeTone('error');
      return setWarningMessage('No se encontro el dia seleccionado.');
    }

    try {
      log('agregarPunto:start', {
        dia: itinerarioSeleccionado.dia,
        puntoId: selectedNewPlace.id,
        nombre: selectedNewPlace.nombre,
      });

      const it = await recalcularItinerario(
        itinerarioSeleccionado.dia,
        itinerarioSeleccionado.lugares,
        null,
        selectedNewPlace,
        modoTransporte,
        getHoraInicioDia(itinerarioSeleccionado.dia),
      );
      if (!isViewActiveRef.current) return;

      const puntoAnadido = it.lugares.some((l) => l.id === selectedNewPlace.id);
      if (!puntoAnadido) {
        log('agregarPunto:limitReached', {
          dia: itinerarioSeleccionado.dia,
          puntoId: selectedNewPlace.id,
        });
        setNoticeTone('error');
        setWarningMessage('Se ha llegado al limite diario de puntos visitables.');
        setSelectedNewPlace(null);
        return;
      }

      log('agregarPunto:done', {
        dia: it.dia,
        lugares: it.lugares.length,
      });

      setItinerarios((prev) => prev.map((x) => (x.dia === it.dia ? it : x)));
      await actualizarGeometryDia(di, it.lugares);
      setSelectedNewPlace(null);
      setSelectedRoutePlaces([]);
      setWarningMessage(null);
    } catch (error) {
      console.error('[MapView] agregarPunto:error', error);
      setNoticeTone('error');
      setWarningMessage('Error al anadir el punto.');
    }
  };

  const sustituirLugarSinRecalcular = async () => {
    if (!itinerarioSeleccionado || selectedRoutePlaces.length !== 2) {
      return setWarningMessage('Selecciona 2 dentro.');
    }
    const [a, b] = selectedRoutePlaces;
    const copia = [...itinerarioSeleccionado.lugares];
    const iA = copia.findIndex((x) => x.id === a.id);
    const iB = copia.findIndex((x) => x.id === b.id);
    if (iA < 0 || iB < 0) return setWarningMessage('No encontrados.');
    [copia[iA], copia[iB]] = [copia[iB], copia[iA]];
    const di = itinerarios.findIndex((it) => it.dia === itinerarioSeleccionado.dia);
    const it = await recalcularItinerario(
      itinerarioSeleccionado.dia,
      copia,
      null,
      null,
      modoTransporte,
      getHoraInicioDia(itinerarioSeleccionado.dia),
    );
    if (!isViewActiveRef.current) return;
    const newIts = [...itinerarios];
    newIts[di] = it;
    setItinerarios(newIts);
    await actualizarGeometryDia(di, it.lugares);
    setSelectedRoutePlaces([]);
    setSelectedNewPlace(null);
    setWarningMessage(null);
  };

  /* ---------- estado de arrastre ---------- */
  const [draggedPlace, setDraggedPlace] = useState<{ place: Lugar; day: number } | null>(null);

  const onPlaceDragStart = (e: React.DragEvent, place: Lugar, day: number) => {
    log('drag:start', { id: place.id, nombre: place.nombre, day });
    e.dataTransfer.effectAllowed = 'move';
    setDraggedPlace({ place, day });
  };

  const onPlaceDragOver = (e: React.DragEvent) => e.preventDefault();

  const onPlaceDrop = async (e: React.DragEvent, target: Lugar, targetDay: number) => {
    e.stopPropagation();
    if (!draggedPlace) return;
    const { place: src, day: srcDay } = draggedPlace;
    if (src.id === target.id) return;
    log('drag:drop', { srcId: src.id, srcDay, targetId: target.id, targetDay });

    if (srcDay === targetDay) {
      await reordenarDentroDelDia(srcDay, src.id, target.id);
    } else {
      await intercambiarEntreDias(srcDay, targetDay, src.id, target.id);
    }
    setDraggedPlace(null);
  };

  const handlePlaceDropToEmpty = async (destDay: number) => {
    if (!draggedPlace || draggedPlace.day === destDay) return;
    log('drag:dropEmpty', { srcId: draggedPlace.place.id, srcDay: draggedPlace.day, destDay });
    await intercambiarEntreDias(draggedPlace.day, destDay, draggedPlace.place.id, null);
    setDraggedPlace(null);
  };

  async function reordenarDentroDelDia(day: number, idA: number, idB: number) {
    log('reordenar:start', { day, idA, idB });
    const di = itinerarios.findIndex((it) => it.dia === day);
    if (di === -1) return;

    const lugares = [...itinerarios[di].lugares];
    const idxA = lugares.findIndex((l) => l.id === idA);
    const idxB = lugares.findIndex((l) => l.id === idB);
    if (idxA === -1 || idxB === -1) return;

    [lugares[idxA], lugares[idxB]] = [lugares[idxB], lugares[idxA]];

    const it = await recalcularItinerario(
      day,
      lugares,
      null,
      null,
      modoTransporte,
      getHoraInicioDia(day),
    );
    if (!isViewActiveRef.current) return;
    log('reordenar:done', { day, lugares: it.lugares.length });
    setItinerarios((prev) => prev.map((x) => (x.dia === it.dia ? it : x)));
    await actualizarGeometryDia(di, it.lugares);
  }

  async function intercambiarEntreDias(srcDay: number, dstDay: number, idSrc: number, idDst: number | null) {
    log('intercambiarEntreDias:start', { srcDay, dstDay, idSrc, idDst });
    const its = [...itinerarios];

    const iSrc = its.findIndex((it) => it.dia === srcDay);
    const iDst = its.findIndex((it) => it.dia === dstDay);
    if (iSrc === -1 || iDst === -1) return;

    const srcPlaces = [...its[iSrc].lugares];
    const dstPlaces = [...its[iDst].lugares];

    const idxSrc = srcPlaces.findIndex((l) => l.id === idSrc);
    if (idxSrc === -1) return;
    const [placeSrc] = srcPlaces.splice(idxSrc, 1);

    if (idDst === null) {
      dstPlaces.push(placeSrc);
    } else {
      const idxDst = dstPlaces.findIndex((l) => l.id === idDst);
      if (idxDst === -1) return;
      const [placeDst] = dstPlaces.splice(idxDst, 1, placeSrc);
      srcPlaces.splice(idxSrc, 0, placeDst);
    }

    const [srcRecalculado, dstRecalculado] = await Promise.all([
      recalcularItinerario(srcDay, srcPlaces, null, null, modoTransporte, getHoraInicioDia(srcDay)),
      recalcularItinerario(dstDay, dstPlaces, null, null, modoTransporte, getHoraInicioDia(dstDay)),
    ]);
    if (!isViewActiveRef.current) return;
    log('intercambiarEntreDias:done', {
      srcDay,
      dstDay,
      srcPlaces: srcRecalculado.lugares.length,
      dstPlaces: dstRecalculado.lugares.length,
    });

    its[iSrc] = srcRecalculado;
    its[iDst] = dstRecalculado;
    setItinerarios(its);

    await Promise.all([
      actualizarGeometryDia(iSrc, srcRecalculado.lugares),
      actualizarGeometryDia(iDst, dstRecalculado.lugares),
    ]);
  }

  async function actualizarGeometryDia(idx: number, lugDia: Lugar[]) {
    log('actualizarGeometryDia:start', { idx, lugares: lugDia.length });
    let coords = lugDia
      .filter(hasValidCoordinates)
      .map((l) => [l.latitud, l.longitud] as [number, number]);
    if (hasValidCoordinates(startingPoint))
      coords = [
        [startingPoint.latitud, startingPoint.longitud],
        ...coords,
        [startingPoint.latitud, startingPoint.longitud],
      ];
    const geo = await obtenerRutaConFallback(coords, modoTransporte);
    if (!isViewActiveRef.current) return;
    log('actualizarGeometryDia:done', { idx, coordinates: geo?.coordinates.length ?? 0 });
    setRutasOSRM((rs) => {
      const c = [...rs];
      c[idx] = geo;
      return c;
    });
  }

  const zoomToLocation = (lat: number, lon: number) => mapRef.current?.setView([lat, lon], 16);

  const createShareLink = async (): Promise<string> => {
    const result = await shareItinerary({
      itinerary: itinerarios,
    });
    setShareUrl(result.url);
    return result.url;
  };

  const handleCopyShareLink = async () => {
    try {
      setCopyingShareLink(true);
      const url = shareUrl || (await createShareLink());
      await navigator.clipboard.writeText(url);
      setNoticeTone('success');
      setWarningMessage('Enlace copiado al portapapeles.');
      setShowShareModal(false);
    } catch (err) {
      console.error('Error copiando enlace:', err);
      setNoticeTone('error');
      setWarningMessage('No se pudo copiar el enlace.');
    } finally {
      if (isViewActiveRef.current) setCopyingShareLink(false);
    }
  };

  const fitMapToItineraryDay = (itineraryIndex: number) => {
    const map = mapRef.current;
    const itinerary = itinerarios[itineraryIndex];
    if (!map || !itinerary) return;

    const routePositions = rutasOSRM[itineraryIndex]?.coordinates?.map(
      ([lon, lat]) => [lat, lon] as LatLngTuple,
    );
    const placePositions = itinerary.lugares.map((lugar) => [lugar.latitud, lugar.longitud] as LatLngTuple);
    const boundaryPositions = [
      dayStarts[itinerary.dia] || startingPoint || null,
      dayEnds[itinerary.dia] || null,
    ]
      .filter(Boolean)
      .map((lugar) => [lugar!.latitud, lugar!.longitud] as LatLngTuple);
    const positions = [...(routePositions?.length ? routePositions : placePositions), ...boundaryPositions].filter(
      ([lat, lon]) => Number.isFinite(lat) && Number.isFinite(lon),
    );

    if (positions.length === 1) {
      map.setView(positions[0], Math.max(map.getZoom(), 14), { animate: false });
      return;
    }

    if (positions.length > 1) {
      map.fitBounds(L.latLngBounds(positions), {
        animate: false,
        paddingTopLeft: [56, 56],
        paddingBottomRight: [56, 56],
      });
    }
  };

  const captureDailyMapImages = async (): Promise<Record<number, string>> => {
    const map = mapRef.current;
    const mapEl = map?.getContainer();
    if (!map || !mapEl) return {};

    const previousDay = selectedDay;
    const previousRoutePlaces = selectedRoutePlaces;
    const previousNewPlace = selectedNewPlace;
    const previousActivePlace = activePlace;
    const previousShowPoiSearch = showPoiSearch;
    const previousShowUserLocation = showUserLocation;
    const previousInlineWidth = mapEl.style.width;
    const previousInlineHeight = mapEl.style.height;
    const previousInlineMaxWidth = mapEl.style.maxWidth;

    const captures: Record<number, string> = {};

    cancelClosePopup();
    setCaptureMode(true);
    setActivePlace(null);
    setSelectedRoutePlaces([]);
    setSelectedNewPlace(null);
    setShowPoiSearch(false);
    setShowUserLocation(false);

    try {
      mapEl.style.width = '980px';
      mapEl.style.height = '590px';
      mapEl.style.maxWidth = 'none';
      map.invalidateSize(false);
      await wait(250);

      for (let index = 0; index < itinerarios.length; index += 1) {
        const itinerary = itinerarios[index];
        if (!itinerary.lugares.length) continue;
        setSelectedDay(itinerary.dia);
        await wait(120);
        fitMapToItineraryDay(index);
        map.invalidateSize(false);
        await wait(900);

        const canvas = await html2canvas(mapEl, {
          backgroundColor: '#e6f1f2',
          logging: false,
          scale: 1.25,
          useCORS: true,
          ignoreElements: (element) => element.classList?.contains('leaflet-control-zoom') ?? false,
        });
        captures[itinerary.dia] = canvas.toDataURL('image/png');
      }
    } finally {
      mapEl.style.width = previousInlineWidth;
      mapEl.style.height = previousInlineHeight;
      mapEl.style.maxWidth = previousInlineMaxWidth;
      map.invalidateSize(false);
      setSelectedDay(previousDay);
      setSelectedRoutePlaces(previousRoutePlaces);
      setSelectedNewPlace(previousNewPlace);
      setActivePlace(previousActivePlace);
      setShowPoiSearch(previousShowPoiSearch);
      setShowUserLocation(previousShowUserLocation);
      setCaptureMode(false);
    }

    return captures;
  };

  const handleDownloadPdf = async () => {
    if (!itinerarios.some((it) => it.lugares.length)) {
      setNoticeTone('error');
      setWarningMessage('No hay una ruta para exportar.');
      return;
    }

    try {
      setDownloadingPdf(true);
      let dayMapImages: Record<number, string> = {};
      try {
        dayMapImages = await captureDailyMapImages();
      } catch (err) {
        console.warn('No se pudieron capturar mapas por dia para el PDF:', err);
      }

      await buildItineraryPdf({
        itinerarios,
        rutasOSRM,
        colores,
        modoTransporte,
        shareUrl,
        dayMapImages,
        createShareLink,
        formatHour: formatearHora,
      });
      setShowShareModal(false);
      setNoticeTone('success');
      setWarningMessage('PDF descargado correctamente.');
    } catch (err) {
      console.error('Error generando PDF:', err);
      setNoticeTone('error');
      setWarningMessage('No se pudo generar el PDF.');
    } finally {
      if (isViewActiveRef.current) setDownloadingPdf(false);
    }
  };

  const handleExportRoute = (format: 'gpx' | 'kml') => {
    if (!itinerarios.some((it) => it.lugares.length)) {
      setNoticeTone('error');
      setWarningMessage('No hay una ruta para exportar.');
      return;
    }

    try {
      setExportingRoute(format);
      if (format === 'gpx') {
        downloadTextFile(
          'ruta-ribeira.gpx',
          buildGpxContent({ itinerarios, rutasOSRM, formatHour: formatearHora, colores }),
          'application/gpx+xml;charset=utf-8',
        );
      } else {
        downloadTextFile(
          'ruta-ribeira.kml',
          buildKmlContent({ itinerarios, rutasOSRM, formatHour: formatearHora, colores }),
          'application/vnd.google-earth.kml+xml;charset=utf-8',
        );
      }
      setShowShareModal(false);
      setNoticeTone('success');
      setWarningMessage(`Archivo ${format.toUpperCase()} descargado correctamente.`);
    } catch (err) {
      console.error(`Error exportando ${format}:`, err);
      setNoticeTone('error');
      setWarningMessage(`No se pudo exportar el archivo ${format.toUpperCase()}.`);
    } finally {
      if (isViewActiveRef.current) setExportingRoute(null);
    }
  };

  const handleSendEmail = async (toEmail: string) => {
    try {
      setSendingEmail(true);
      await shareItinerary({
        email: toEmail,
        itinerary: itinerarios,
      });
      alert('¡Correo enviado con éxito! Revisa tu bandeja de entrada.');
    } catch (err) {
      console.error('Error enviando correo:', err);
      alert('Hubo un problema al enviar el correo. Inténtalo más tarde.');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleStartOver = () => {
    log('startOver:click', {
      itineraryDays: itinerarios.length,
      selectedDay,
      pendingGeometries: rutasOSRM.length,
    });
    isViewActiveRef.current = false;
    cancelClosePopup();
    stopUserLocationTracking();
    resetItinerary();
    setRutasOSRM([]);
    setSelectedDay(null);
    setSelectedRoutePlaces([]);
    setSelectedNewPlace(null);
    setWarningMessage(null);
    setDraggedDay(null);
    setDraggedPlace(null);
    setActivePlace(null);
    setPlaceCardPlace(null);
    setDayStartTimes({});
    isDayStartTimePickerOpen.current = false;
    isDayStartEditorPinnedOpen.current = false;
    setDayStartEditor(null);
    navigate('/', { replace: true, state: { reset: true } });
  };

  const handleDayStartSubmit = async (
    event: React.FormEvent<HTMLFormElement>,
    dia: number,
  ) => {
    event.preventDefault();
    event.stopPropagation();

    const formData = new FormData(event.currentTarget);
    const horaInicio = normalizarHoraInicioRuta(String(formData.get('horaInicio') ?? ''));
    const di = itinerarios.findIndex((it) => it.dia === dia);
    if (di === -1) return;

    const lugaresOriginales = itinerarios[di].lugares;
    setDayStartTimes((prev) => ({ ...prev, [dia]: horaInicio }));
    isDayStartTimePickerOpen.current = false;
    isDayStartEditorPinnedOpen.current = false;
    setDayStartEditor(null);

    try {
      const it = await recalcularItinerario(
        dia,
        lugaresOriginales,
        null,
        null,
        modoTransporte,
        horaInicio,
      );
      if (!isViewActiveRef.current) return;

      setItinerarios((prev) => prev.map((x) => (x.dia === it.dia ? it : x)));
      await actualizarGeometryDia(di, it.lugares);

      const eliminados = Math.max(0, lugaresOriginales.length - it.lugares.length);
      setSelectedDay(dia);
      setSelectedRoutePlaces([]);
      setSelectedNewPlace(null);
      setNoticeTone('success');
      setWarningMessage(
        eliminados > 0
          ? 'Hora de inicio actualizada. La ruta se ha ajustado al limite de las 19:00.'
          : 'Hora de inicio actualizada.',
      );
    } catch (error) {
      console.error('[MapView] cambiarHoraInicio:error', error);
      setNoticeTone('error');
      setWarningMessage('No se pudo cambiar la hora de inicio.');
    }
  };

  const estadoHorarioActivo = activePlace ? obtenerEstadoHorario(activePlace) : null;
  const activePlaceImage = activePlace ? getImageUrl(activePlace.fotos) : undefined;

  /* ------------------------------------------------------------------ */
  /*  RENDER                                                            */
  /* ------------------------------------------------------------------ */
  return (
    <div className={styles.container}>
      <div className={styles.languageSelector}></div>

      <div
        className={styles.sidebar}
        style={{ '--mapview-day-count': Math.min(Math.max(itinerarios.length, 1), 5) } as React.CSSProperties}
      >
        <div className={styles.dayRouteScroller}>
          <div className={styles.dayRouteContent}>
            <div className={styles.dayTabs}>
          {(itinerarios || []).map((it, idx) => {
            const color = colores[idx % colores.length];
            const activo = selectedDay === it.dia;

            return (
              <div
                key={it.dia}
                className={styles.dayTabItem}
                onMouseEnter={(event) => openDayStartEditor(event.currentTarget, it.dia)}
                onMouseLeave={scheduleCloseDayStartEditor}
                onFocus={(event) => openDayStartEditor(event.currentTarget, it.dia)}
              >
              <button
                className={`${styles.dayTabButton} ${activo ? styles.activeDay : ''}`}
                style={{ backgroundColor: color }}
                onClick={() => setSelectedDay(it.dia)}
                draggable
                onDragStart={(e) => handleDragStart(e, it.dia)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, it.dia)}
              >
                DÍA&nbsp;{it.dia}
              </button>
              </div>
            );
          })}
        </div>

            <div className={styles.dragHint}>Arrastra las tarjetas para modificar la ruta</div>

            <div className={styles.itineraryGrid}>
            {itinerarios.map((it) => (
              <div
                key={it.dia}
                className={`${styles.dayColumn} ${
                  draggedPlace && draggedPlace.day !== it.dia ? styles.dayColumnDropTarget : ''
                }`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => handlePlaceDropToEmpty(it.dia)}
              >
                {it.lugares.map((l) => {
                  const imageUrl = getImageUrl(l.fotos);

                  return (
                    <div
                      key={l.id}
                      ref={(el) => (itineraryItemRefs.current[l.id] = el)}
                      className={`${styles.placeCard} ${isMarkerSelected(l) ? styles.activeCard : ''} ${
                        draggedPlace?.place.id === l.id ? styles.draggingCard : ''
                      }`}
                      draggable
                      title="Arrastra este punto a otro dia para cambiar la ruta"
                      onDragStart={(e) => onPlaceDragStart(e, l, it.dia)}
                      onDragOver={onPlaceDragOver}
                      onDrop={(e) => onPlaceDrop(e, l, it.dia)}
                      onDragEnd={() => setDraggedPlace(null)}
                    >
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={l.nombre}
                          className={styles.placeThumbLarge}
                          onClick={() => {
                            setSelectedDay(it.dia);
                            setTimeout(() => {
                              if (hasValidCoordinates(l)) {
                                mapRef.current?.setView([l.latitud, l.longitud], mapRef.current?.getMaxZoom());
                              }
                              handleRouteMarkerClick(l);
                            }, 300);
                          }}
                        />
                      ) : (
                        <button
                          type="button"
                          className={styles.placeThumbLargePlaceholder}
                          aria-label={`Ver ${l.nombre} en el mapa`}
                          onClick={() => {
                            setSelectedDay(it.dia);
                            setTimeout(() => {
                              if (hasValidCoordinates(l)) {
                                mapRef.current?.setView([l.latitud, l.longitud], mapRef.current?.getMaxZoom());
                              }
                              handleRouteMarkerClick(l);
                            }, 300);
                          }}
                        />
                      )}

                      <div className={styles.placeTitleAndTime}>
                        <div className={styles.placeTitle} title={l.nombre}>
                          {l.nombre}
                        </div>
                        <div className={styles.placeTimes}>
                          <span>Llegada: {formatearHora(l.horaLlegada)}</span>
                          <span>Salida:&nbsp;&nbsp;{formatearHora(l.horaSalida)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
          </div>
        </div>
      </div>

      {dayStartEditor && (
        <form
          ref={dayStartPanelRef}
          className={styles.dayStartPanel}
          style={{ left: dayStartEditor.left, top: dayStartEditor.top }}
          onSubmit={(event) => handleDayStartSubmit(event, dayStartEditor.dia)}
          onClick={(event) => event.stopPropagation()}
          onMouseEnter={cancelCloseDayStartEditor}
          onMouseLeave={scheduleCloseDayStartEditor}
          onFocus={cancelCloseDayStartEditor}
        >
          <label htmlFor={`day-start-${dayStartEditor.dia}`}>Hora de inicio</label>
          <input
            key={`${dayStartEditor.dia}-${getHoraInicioDia(dayStartEditor.dia)}`}
            id={`day-start-${dayStartEditor.dia}`}
            name="horaInicio"
            type="time"
            min={MIN_ROUTE_START_TIME}
            max={MAX_ROUTE_END_TIME}
            defaultValue={getHoraInicioDia(dayStartEditor.dia)}
            onPointerDown={keepDayStartEditorOpenForTimePicker}
            onClick={(event) => {
              keepDayStartEditorOpenForTimePicker();
              abrirSelectorHora(event.currentTarget);
            }}
            onFocus={(event) => {
              keepDayStartEditorOpenForTimePicker();
              abrirSelectorHora(event.currentTarget);
            }}
            onChange={(event) => {
              const input = event.currentTarget;
              keepDayStartEditorOpenForTimePicker();
              isDayStartEditorPinnedOpen.current = true;
              window.setTimeout(() => {
                input.blur();
                cancelCloseDayStartEditor();
              }, 0);
            }}
            onBlur={releaseDayStartTimePicker}
          />
         
          <button type="submit">Aplicar</button>
        </form>
      )}

      <div className={styles.main}>
        {warningMessage && (
          <div
            className={`${styles.warning} ${
              noticeTone === 'success' ? styles.warningSuccess : styles.warningError
            }`}
          >
            <p>{warningMessage}</p>
            <button onClick={() => setWarningMessage(null)}>Cerrar</button>
          </div>
        )}

        <div className={styles.buttonContainer}>
          <div className={styles.buttonWrapper}>
            <button onClick={handleStartOver} className={styles.enabledButton}>
              Crear desde 0
            </button>
          </div>

          <div
            className={styles.buttonWrapper}
            onMouseEnter={() => {
              if (!selectedNewPlace || !itinerarioSeleccionado) setShowAddTooltip(true);
            }}
            onMouseLeave={() => setShowAddTooltip(false)}
          >
            <button
              onClick={agregarPuntoSeleccionado}
              disabled={!selectedNewPlace || !itinerarioSeleccionado}
              className={!selectedNewPlace || !itinerarioSeleccionado ? styles.disabledButton : styles.enabledButton}
            >
              A&ntilde;adir Punto
            </button>
            {(!selectedNewPlace || !itinerarioSeleccionado) && showAddTooltip && (
              <div className={styles.helpTooltip}>
                <p>Selecciona un punto fuera de la ruta para a&ntilde;adirlo al d&iacute;a activo.</p>
              </div>
            )}
          </div>

          <div
            className={styles.buttonWrapper}
            onMouseEnter={() => {
              if (!(selectedRoutePlaces.length === 1 && selectedNewPlace)) setShowRecalculateTooltip(true);
            }}
            onMouseLeave={() => setShowRecalculateTooltip(false)}
          >
            <button
              onClick={intercambiarLugares}
              disabled={!(selectedRoutePlaces.length === 1 && selectedNewPlace)}
              className={
                !(selectedRoutePlaces.length === 1 && selectedNewPlace)
                  ? styles.disabledButton
                  : styles.enabledButton
              }
            >
              Cambiar Ruta
            </button>
            {!(selectedRoutePlaces.length === 1 && selectedNewPlace) && showRecalculateTooltip && (
              <div className={styles.helpTooltip}>
                <p>Selecciona 1 punto de la ruta y 1 fuera para intercambiarlos.</p>
              </div>
            )}
          </div>

          <div
            className={styles.buttonWrapper}
            onMouseEnter={() => {
              if (selectedRoutePlaces.length !== 2) setShowSustituirTooltip(true);
            }}
            onMouseLeave={() => setShowSustituirTooltip(false)}
          >
            <button
              onClick={sustituirLugarSinRecalcular}
              disabled={selectedRoutePlaces.length !== 2}
              className={selectedRoutePlaces.length !== 2 ? styles.disabledButton : styles.enabledButton}
            >
              Intercambiar puntos en la ruta
            </button>
            {selectedRoutePlaces.length !== 2 && showSustituirTooltip && (
              <div className={styles.helpTooltip}>
                <p>Selecciona 2 lugares de la ruta para intercambiarlos de orden.</p>
              </div>
            )}
          </div>

          <div
            className={styles.buttonWrapper}
            onMouseEnter={() => {
              if (!(selectedRoutePlaces.length === 1 && !selectedNewPlace)) setShowDeleteTooltip(true);
            }}
            onMouseLeave={() => setShowDeleteTooltip(false)}
          >
            <button
              onClick={eliminarPuntoSeleccionado}
              disabled={!(selectedRoutePlaces.length === 1 && !selectedNewPlace)}
              className={
                !(selectedRoutePlaces.length === 1 && !selectedNewPlace)
                  ? styles.disabledButton
                  : styles.enabledButton
              }
            >
              Eliminar Punto
            </button>
            {!(selectedRoutePlaces.length === 1 && !selectedNewPlace) && showDeleteTooltip && (
              <div className={styles.helpTooltip}>
                <p>Selecciona 1 punto de la ruta para eliminarlo.</p>
              </div>
            )}
          </div>

          <div className={styles.buttonWrapper}>
            <button
              onClick={() => setShowShareModal(true)}
              disabled={sendingEmail}
              className={sendingEmail ? styles.disabledButton : styles.enabledButton}
            >
              {sendingEmail ? 'Enviando...' : 'Compartir'}
            </button>
          </div>
        </div>

        <div className={`${styles.poiSearchControl} ${showPoiSearch ? styles.poiSearchControlOpen : ''}`}>
          <button
            type="button"
            className={styles.poiSearchToggle}
            onClick={() => setShowPoiSearch((open) => !open)}
            aria-label={showPoiSearch ? 'Cerrar buscador' : 'Buscar'}
            title={showPoiSearch ? 'Cerrar buscador' : 'Buscar '}
          >
            {showPoiSearch ? <CloseIcon aria-hidden="true" /> : <SearchIcon aria-hidden="true" />}
          </button>

          {showPoiSearch && (
            <div className={styles.poiSearchPanel}>
              <div className={styles.poiSearchInputWrap}>
                <SearchIcon aria-hidden="true" />
                <input
                  ref={poiSearchInputRef}
                  type="search"
                  value={poiSearchQuery}
                  onChange={(e) => setPoiSearchQuery(e.target.value)}
                  placeholder="Buscar punto turistico"
                  aria-label="Buscar punto turistico por nombre"
                />
              </div>

              <div className={styles.poiSearchResults}>
                {resultadosBusquedaPoi.length ? (
                  resultadosBusquedaPoi.map((poi) => {
                    const diaRuta = itinerarios.find((it) =>
                      it.lugares.some((l) => l.id === poi.id),
                    );

                    return (
                      <button
                        key={poi.id}
                        type="button"
                        className={styles.poiSearchResult}
                        onClick={() => seleccionarPoiDesdeBusqueda(poi)}
                      >
                        <span>{poi.nombre}</span>
                        <small>{diaRuta ? `Dia ${diaRuta.dia}` : 'Disponible'}</small>
                      </button>
                    );
                  })
                ) : (
                  <p className={styles.poiSearchEmpty}>No hay resultados.</p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className={styles.locationControl}>
          <button
            type="button"
            onClick={toggleUserLocation}
            className={`${styles.locationButton} ${
              showUserLocation ? styles.locationButtonActive : ''
            }`}
            aria-pressed={showUserLocation}
            title={
              showUserLocation || isLocatingUser
                ? 'Ocultar tu ubicacion'
                : 'Mostrar tu ubicacion'
            }
          >
            <LocationButtonIcon disabled={showUserLocation} />
            <span>
              {isLocatingUser
                ? 'Localizando...'
                : showUserLocation
                  ? 'Ocultar ubicacion'
                  : 'Mi ubicacion'}
            </span>
          </button>
        </div>

        <MapContainer center={centroMapa} zoom={12} preferCanvas className={styles.map}>
          <SetMapRef mapRef={mapRef} />
          <MapClickHandler onClick={() => setActivePlace(null)} />

          <TileLayer
            attribution="&copy; OpenStreetMap contributors"
            url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {showUserLocation && userLocation && (
            <>
              {userLocation.precision > 0 && (
                <Circle
                  center={[userLocation.latitud, userLocation.longitud]}
                  radius={userLocation.precision}
                  pathOptions={{
                    color: '#0070C8',
                    fillColor: '#0070C8',
                    fillOpacity: 0.12,
                    opacity: 0.35,
                    weight: 1,
                  }}
                />
              )}
              <CircleMarker
                center={[userLocation.latitud, userLocation.longitud]}
                radius={8}
                pathOptions={{
                  color: '#ffffff',
                  fillColor: '#0070C8',
                  fillOpacity: 1,
                  weight: 3,
                }}
              >
                {!captureMode && (
                  <Popup
                    autoClose={false}
                    closeOnClick={false}
                    closeButton={false}
                    offset={[0, -10]}
                    className={styles.fondo}
                  >
                    <div className={styles.tooltipContent}>
                      <h3>Tu ubicacion</h3>
                      {userLocation.precision > 0 && (
                        <p>Precision aproximada: {Math.round(userLocation.precision)} m</p>
                      )}
                    </div>
                  </Popup>
                )}
              </CircleMarker>
            </>
          )}

          {hasValidCoordinates(startingPoint) && (
            <Marker position={[startingPoint.latitud, startingPoint.longitud]} icon={houseIcon}>
              {!captureMode && (
                <Popup
                  autoClose={false}
                  closeOnClick={false}
                  closeButton={false}
                  offset={[0, -40]}
                  className={styles.fondo}
                >
                  <div className={styles.tooltipContent}>
                    <h3>Punto de Inicio</h3>
                    <p>{startingPoint.nombre}</p>
                  </div>
                </Popup>
              )}
            </Marker>
          )}

          {selectedDay !== null && (
            <>
              {hasValidCoordinates(dayStarts[selectedDay]) && (
                <Marker
                  position={[dayStarts[selectedDay]!.latitud, dayStarts[selectedDay]!.longitud]}
                  icon={houseIcon}
                >
                  {!captureMode && (
                    <Popup
                      autoClose={false}
                      closeOnClick={false}
                      closeButton={false}
                      offset={[0, -40]}
                      className={styles.fondo}
                    >
                      <div className={styles.tooltipContent}>
                        <h3>Inicio día {selectedDay}</h3>
                      </div>
                    </Popup>
                  )}
                </Marker>
              )}

              {hasValidCoordinates(dayEnds[selectedDay]) && (
                <Marker
                  position={[dayEnds[selectedDay]!.latitud, dayEnds[selectedDay]!.longitud]}
                  icon={houseIcon}
                >
                  {!captureMode && (
                    <Popup
                      autoClose={false}
                      closeOnClick={false}
                      closeButton={false}
                      offset={[0, -40]}
                      className={styles.fondo}
                    >
                      <div className={styles.tooltipContent}>
                        <h3>Fin día {selectedDay}</h3>
                      </div>
                    </Popup>
                  )}
                </Marker>
              )}
            </>
          )}

          {selectedDay !== null &&
            rutasOSRM.map((geo, di) => {
              if (!geo || !itinerarios[di]) return null;
              if (itinerarios[di].dia !== selectedDay) return null;

              const color = colores[di % colores.length];
              const pos = geo.coordinates.map(([lon, lat]) => [lat, lon] as LatLngTuple);

              return (
                <React.Fragment key={di}>
                  <Polyline positions={pos} color={color} weight={4} opacity={1} />

                  {itinerarios[di].lugares.filter(hasValidCoordinates).map((l, i) => {
                    let ico = getIconForLugar(l);
                    if (isMarkerSelected(l)) ico = blueIcon;
                    const numbered = createNumberedIcon(i + 1, ico, color, l.icon);

                    return (
                      <Marker
                        key={l.id}
                        position={[l.latitud, l.longitud]}
                        icon={numbered}
                        zIndexOffset={1000}
                        eventHandlers={{
                          click: () => handleRouteMarkerClick(l),
                          mouseover: () => {
                            if (captureMode) return;
                            cancelClosePopup();
                            setActivePlace(l);
                          },
                          mouseout: () => {
                            if (captureMode) return;
                            scheduleClosePopup();
                          },
                        }}
                      />
                    );
                  })}
                </React.Fragment>
              );
            })}

          {!captureMode && (
            <ClusterLayer
              puntos={puntosVisiblesParaCambios}
              selectedRoutePlaces={selectedRoutePlaces}
              selectedNewPlace={selectedNewPlace}
              onMarkerClick={handleExternalMarkerClick}
              onMarkerMouseOver={(p) => {
                cancelClosePopup();
                setActivePlace(p);
              }}
              onMarkerMouseOut={() => {
                scheduleClosePopup();
              }}
            />
          )}

          {!captureMode && activePlace && hasValidCoordinates(activePlace) && (
            <Popup
              position={[activePlace.latitud, activePlace.longitud]}
              autoClose={false}
              closeOnClick={false}
              closeButton={false}
              offset={[0, -40]}
              className={styles.fondo}
            >
              <div
                className={styles.popupContent}
                onMouseEnter={() => cancelClosePopup()}
                onMouseLeave={() => scheduleClosePopup()}
              >
                <h3>{activePlace.nombre}</h3>
                {activePlaceImage && <img src={activePlaceImage} alt={activePlace.nombre} />}
                {estadoHorarioActivo && (
                  <div className={styles.popupSchedule}>
                    {estadoHorarioActivo.fechaRuta && (
                      <p className={styles.popupScheduleDay}>{estadoHorarioActivo.fechaRuta}</p>
                    )}
                    <p>
                      <strong>Visita estimada:</strong> {estadoHorarioActivo.visitaLabel}
                    </p>
                    <p>
                      <strong>Horario del dia:</strong>{' '}
                      {estadoHorarioActivo.horarioDia || estadoHorarioActivo.horario || 'No disponible'}
                    </p>
                    {estadoHorarioActivo.horario &&
                      estadoHorarioActivo.horarioDia &&
                      estadoHorarioActivo.horario !== estadoHorarioActivo.horarioDia && (
                        <p className={styles.popupScheduleWeekly}>
                          <strong>Semanal:</strong> {estadoHorarioActivo.horario}
                        </p>
                      )}
                    {estadoHorarioActivo.aviso && (
                      <p
                        className={
                          estadoHorarioActivo.cerrado
                            ? styles.popupScheduleClosed
                            : styles.popupScheduleOpen
                        }
                      >
                        {estadoHorarioActivo.aviso}
                      </p>
                    )}
                  </div>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setPlaceCardPlace(activePlace);
                  }}
                >
                  Ver detalles
                </button>
              </div>
            </Popup>
          )}
        </MapContainer>
      </div>

      {placeCardPlace && (
        <div className={styles.modalOverlay} onClick={() => setPlaceCardPlace(null)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <PlaceCard lugar={placeCardPlace} onClose={() => setPlaceCardPlace(null)} />
          </div>
        </div>
      )}

      {showShareModal && (
        <div className={styles.modalOverlay} onClick={() => setShowShareModal(false)}>
          <div
            className={`${styles.modalContent} ${styles.emailModal}`}
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Compartir ruta</h3>

            <div className={styles.shareOptions}>
              <button
                type="button"
                className={styles.shareOptionButton}
                disabled={downloadingPdf}
                onClick={handleDownloadPdf}
              >
                {downloadingPdf ? 'Generando PDF...' : 'Descargar PDF'}
              </button>
              <div className={styles.shareExportGrid}>
                <button
                  type="button"
                  className={`${styles.shareOptionButton} ${styles.shareOptionButtonSecondary}`}
                  disabled={exportingRoute !== null}
                  onClick={() => handleExportRoute('gpx')}
                >
                  {exportingRoute === 'gpx' ? 'Exportando...' : 'Exportar GPX'}
                </button>
                <button
                  type="button"
                  className={`${styles.shareOptionButton} ${styles.shareOptionButtonSecondary}`}
                  disabled={exportingRoute !== null}
                  onClick={() => handleExportRoute('kml')}
                >
                  {exportingRoute === 'kml' ? 'Exportando...' : 'Exportar KML'}
                </button>
              </div>
              <button
                type="button"
                className={`${styles.shareOptionButton} ${styles.shareOptionButtonSecondary}`}
                disabled={copyingShareLink}
                onClick={handleCopyShareLink}
              >
                {copyingShareLink ? 'Copiando...' : 'Copiar enlace'}
              </button>
            </div>

            <div className={styles.shareDivider}>Enviar por email</div>

            <input
              type="email"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              placeholder="tu@email.com"
              className={styles.emailInput}
            />

            <div className={styles.modalActions}>
              <button onClick={() => setShowShareModal(false)}>Cancelar</button>
              <button
                disabled={!/\S+@\S+\.\S+/.test(recipientEmail) || sendingEmail}
                onClick={() => {
                  setShowShareModal(false);
                  handleSendEmail(recipientEmail);
                }}
              >
                {sendingEmail ? 'Enviando...' : 'Enviar email'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapView;

